import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Attendance, AttendanceService, Channel, ChannelService, Department, DepartmentService, MonitoringColumn, SocketService, STATUS_COLUMNS_CONFIG, User, UserService, ViewMode } from 'lib-trend-core';
import { catchError, concatMap, distinctUntilChanged, EMPTY, filter, interval, mergeMap, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DEFAULT_MONITORING_PREFERENCES, MonitoringPreferences, MonitoringSettingsDialogComponent } from './monitoring-settings-dialog/monitoring-settings-dialog.component';

@Component({
    selector: 'app-attendance-monitoring',
    templateUrl: './attendance-monitoring.component.html',
    styleUrls: ['./attendance-monitoring.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendanceMonitoringComponent extends AbstractComponent {
  public statusColumns: MonitoringColumn[] = [];
  public groupColumns: MonitoringColumn[] = [];
  public viewMode: ViewMode = 'user';
  public listChannel: Channel[] = [];
  public labelChannel = this.t('attendance.monitoring.channels');
  public reloadTrigger = 0;
  public tickTrigger = 0;

  public users: Array<User> = new Array<User>();
  public departments: Array<Department> = new Array<Department>();

  public totalOpen = 0;
  public totalDelayed = 0;
  public alertsEnabled = true;
  public soundAlertEnabled = false;
  public delayThresholdSeconds: number;

  private globalMetricsLoad$ = new Subject<void>();
  private monitoringPreferencesStorageKey: string;
  private previousTotalDelayed = 0;
  private alertMutedUntil = 0;
  private audioContext?: AudioContext;

  private readonly RELOAD_MUTE_MS = 3000;

  get effectiveThresholdSeconds(): number {
    return this.alertsEnabled ? this.delayThresholdSeconds : Number.POSITIVE_INFINITY;
  }

  constructor(
    injector: Injector,
    private channelService: ChannelService,
    public socketService: SocketService,
    public userService: UserService,
    public departmentService: DepartmentService,
    private attendanceService: AttendanceService,
    private dialog: MatDialog,
  ) {
    super(injector);
    this.monitoringPreferencesStorageKey = this.constants?.['monitoringPreferences'] ?? 'monitoringPreferences';
    const prefs = this.handleMonitoringStorage('get') as MonitoringPreferences;
    this.alertsEnabled = prefs.alertsEnabled;
    this.soundAlertEnabled = prefs.soundAlertEnabled;
    this.delayThresholdSeconds = prefs.delayThresholdSeconds;
    this.setupForm();
  }

  private handleMonitoringStorage(action: 'get' | 'set' | 'remove', value?: MonitoringPreferences): MonitoringPreferences | null {
    try {
      if (action === 'get') {
        const raw = localStorage.getItem(this.monitoringPreferencesStorageKey);
        if (!raw) return { ...DEFAULT_MONITORING_PREFERENCES };
        return { ...DEFAULT_MONITORING_PREFERENCES, ...JSON.parse(raw) };
      }
      if (action === 'remove') {
        localStorage.removeItem(this.monitoringPreferencesStorageKey);
        return null;
      }
      if (value) localStorage.setItem(this.monitoringPreferencesStorageKey, JSON.stringify(value));
      return null;
    } catch (err) {
      console.error(`Monitoring preferences storage operation failed: ${action}`, err);
      return null;
    }
  }

  ngOnInit(): void {
    this.globalMetricsLoad$.pipe(
      switchMap(() => {
        const params: { channel?: string; threshold: number } = { threshold: this.delayThresholdSeconds };
        if (this.channelFilter) params.channel = this.channelFilter;
        return this.attendanceService.getMonitoringSummary(params)
          .pipe(catchError(() => EMPTY));
      }),
      takeUntil(this.destroy$),
    ).subscribe((summary: { total: number; delayed: number }) => {
      this.totalOpen = summary.total;
      const delayed = this.alertsEnabled ? summary.delayed : 0;
      const muted = Date.now() < this.alertMutedUntil;
      if (!muted && delayed > this.previousTotalDelayed) this.playDelayAlert();
      this.previousTotalDelayed = delayed;
      this.totalDelayed = delayed;
    });

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.tickTrigger++);

    interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadGlobalMetrics());

    this.userService.getList()
      .pipe(
        takeUntil(this.destroy$),
        tap((users: Array<User>) => {
          this.users = users;
        }),
        mergeMap(() => this.channelService.getList()),
        tap((channels) => {
          this.listChannel = channels;
        }),
        mergeMap(() => this.departmentService.getList()),
      )
      .subscribe((departments) => {
        this.departments = departments;
        this.buildStatusColumns();
        this.loadGlobalMetrics();
      });
    this.configSocket();
  }

  override ngOnDestroy(): void {
    this.audioContext?.close().catch(() => { /* ignore */ });
    this.audioContext = undefined;
    super.ngOnDestroy();
  }

  openSettingsDialog(): void {
    const ref = this.dialog.open(MonitoringSettingsDialogComponent, {
      width: '500px',
    });
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((prefs: MonitoringPreferences | null | undefined) => {
        if (!prefs) return;
        this.alertsEnabled = prefs.alertsEnabled;
        this.soundAlertEnabled = prefs.soundAlertEnabled;
        this.delayThresholdSeconds = prefs.delayThresholdSeconds;
      });
  }

  setViewMode(mode: ViewMode): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.muteAlertDuringReload();
    this.buildStatusColumns();
  }

  searchByChannel(channel: Channel): void {
    this.labelChannel = channel ? channel.name : this.t('attendance.monitoring.channels');
    this.formGroup.get('channel')!.setValue(channel ? channel._id : null);
  }

  get channelFilter(): string | null {
    return this.formGroup.get('channel')?.value ?? null;
  }

  private loadGlobalMetrics(): void {
    this.globalMetricsLoad$.next();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({ channel: [null] });

    this.formGroup.get('channel')!.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.muteAlertDuringReload();
      this.buildStatusColumns();
      this.reloadTrigger++;
      this.loadGlobalMetrics();
    });
  }

  private buildStatusColumns(): void {
    this.statusColumns = STATUS_COLUMNS_CONFIG.map((cfg) => ({
      type: 'status' as const,
      key: cfg.status,
      label: cfg.label,
      icon: cfg.icon,
      colorClass: cfg.colorClass,
      filterParams: { status: cfg.status },
    }));

    if (this.viewMode === 'user') {
      this.groupColumns = this.users.map((user) => ({
        type: 'user' as const,
        key: `user-${user._id}`,
        label: user.name,
        icon: 'ri-user-line',
        colorClass: 'bg-primary',
        filterParams: { user: user._id },
      }));
    } else {
      this.groupColumns = this.departments.map((department) => ({
        type: 'department' as const,
        key: `department-${department._id}`,
        label: department.name,
        icon: 'ri-building-4-line',
        colorClass: 'bg-secondary',
        filterParams: { department: department._id },
        channelName: department.channel?.name,
      }));
    }
  }

  private muteAlertDuringReload(): void {
    this.alertMutedUntil = Date.now() + this.RELOAD_MUTE_MS;
  }

  private playDelayAlert(): void {
    if (!this.alertsEnabled || !this.soundAlertEnabled) return;
    try {
      this.audioContext ??= new (window.AudioContext || (window as any).webkitAudioContext)();
      const ctx = this.audioContext;
      const now = ctx.currentTime;
      // Two descending beeps (880 Hz → 660 Hz) — alert pattern, distinct from message sound.
      [{ freq: 880, start: 0 }, { freq: 660, start: 0.18 }].forEach(({ freq, start }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(0.25, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + 0.15);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + 0.16);
      });
    } catch { }
  }

  /*********************************************************************************
   ***************************** Socket Methods - init *****************************
   *********************************************************************************/
  private attendanceQueue$ = new Subject<Attendance>();

  private setupAttendanceQueue() {
    this.attendanceQueue$.pipe(
      concatMap(() => {
        this.reloadTrigger++;
        this.loadGlobalMetrics();
        return of(null);
      }),
      catchError(() => EMPTY),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private configSocket(): void {
    const code = super.getCodeCompanyCurrentUser();
    const event = `event_attendances_${code}`;

    this.setupAttendanceQueue();

    this.socketService.joinEvent(event);
    this.socketService.listen(event)
      .pipe(
        catchError(() => EMPTY),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => this.attendanceQueue$.next(data?.attendance ?? data));

    this.socketService.getConnectedStatus()
      .pipe(
        filter((connected) => connected),
        catchError(() => EMPTY),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.reloadTrigger++;
        this.loadGlobalMetrics();
      });
  }
  /*********************************************************************************
   ***************************** Socket Methods - end  *****************************
   *********************************************************************************/
}
